<template>
  <div class="guestbook-intake" :class="{ 'guestbook-intake--guest': stage === 'guest' && !contextLoading && !contextError }">
    <div class="guestbook-intake__orb guestbook-intake__orb--one" aria-hidden="true"></div>
    <div class="guestbook-intake__orb guestbook-intake__orb--two" aria-hidden="true"></div>

    <div v-if="contextLoading" class="guestbook-loading">
      <v-progress-circular indeterminate color="white" size="54" width="5" />
      <div>Gästebuch wird vorbereitet …</div>
    </div>

    <div v-else-if="contextError" class="guestbook-stage guestbook-stage--centered">
      <v-card class="staff-card pa-7 pa-sm-10" rounded="xl">
        <div class="staff-card__icon staff-card__icon--error mb-5">
          <v-icon color="red darken-1" size="38">mdi-alert-circle-outline</v-icon>
        </div>
        <h1 class="staff-card__title">Gästebuch nicht verfügbar</h1>
        <p class="staff-card__copy">{{ contextError }}</p>
        <v-btn x-large block depressed rounded color="primary" :loading="contextLoading" @click="loadContext">
          Erneut versuchen
        </v-btn>
        <v-btn text block class="mt-3" @click="leaveToDashboard">Zurück zum Dashboard</v-btn>
      </v-card>
    </div>

    <div v-else-if="stage === 'start'" class="guestbook-stage guestbook-stage--centered">
      <v-card class="staff-card pa-7 pa-sm-10" rounded="xl">
        <div class="staff-brand mb-8">
          <div class="staff-brand__mark">
            <v-icon color="white" size="34">mdi-book-open-page-variant-outline</v-icon>
          </div>
          <div>
            <div class="staff-eyebrow">TRAINER · IPAD</div>
            <h1 class="staff-card__title mb-0">Gästebuch Eintrag</h1>
          </div>
        </div>

        <p class="staff-card__copy staff-card__copy--lead">
          Bereite das iPad für deinen Gast vor. Nach dem Ausfüllen gelangst du direkt zur Account-Zuordnung und zum Erstgespräch.
        </p>

        <div class="staff-context mb-8">
          <div class="staff-context__item">
            <v-icon color="teal darken-1">mdi-map-marker-outline</v-icon>
            <div>
              <small>Studio</small>
              <strong>{{ context.studio.name }}</strong>
            </div>
          </div>
          <div class="staff-context__item">
            <v-icon color="teal darken-1">mdi-account-outline</v-icon>
            <div>
              <small>Trainer</small>
              <strong>{{ trainerDisplayName }}</strong>
            </div>
          </div>
        </div>

        <div class="staff-flow mb-8">
          <div class="staff-flow__step"><span>1</span><strong>iPad übergeben</strong></div>
          <v-icon color="grey lighten-1">mdi-chevron-right</v-icon>
          <div class="staff-flow__step"><span>2</span><strong>Gast füllt aus</strong></div>
          <v-icon color="grey lighten-1">mdi-chevron-right</v-icon>
          <div class="staff-flow__step"><span>3</span><strong>Termin übernehmen</strong></div>
        </div>

        <v-btn x-large block depressed rounded color="primary" class="staff-primary-btn" @click="beginGuestMode">
          Gästebuch öffnen
          <v-icon right>mdi-arrow-right</v-icon>
        </v-btn>
        <v-btn text block class="mt-3" @click="leaveToDashboard">Abbrechen</v-btn>
      </v-card>
    </div>

    <div v-else-if="stage === 'guest'" class="guestbook-stage guestbook-stage--guest">
      <v-card class="guest-card" rounded="xl">
        <header class="guest-header">
          <div class="guest-header__brand">
            <div class="guest-header__mark">
              <v-icon color="white" size="25">mdi-book-open-page-variant-outline</v-icon>
            </div>
            <div>
              <div class="guest-eyebrow">GÄSTEBUCH</div>
              <strong>{{ context.studio.name || "Dein Studio" }}</strong>
            </div>
          </div>

          <div class="guest-protection">
            <span v-if="handoffLocked" class="guest-protection__label">Sperre aktiv</span>
            <v-btn
              icon
              class="guest-protection__button"
              :class="{ 'guest-protection__button--locked': handoffLocked }"
              :aria-label="handoffLocked ? 'Trainer-Sperre deaktivieren' : 'Trainer-Sperre aktivieren'"
              @click="toggleProtection">
              <v-icon>{{ handoffLocked ? "mdi-lock" : "mdi-lock-open-variant-outline" }}</v-icon>
            </v-btn>
          </div>
        </header>

        <div class="guest-experience">
          <aside class="guest-story">
            <div class="guest-story__eyebrow">
              <v-icon color="#8ce8ba" size="18">mdi-sparkles</v-icon>
              <span>Willkommen</span>
            </div>
            <h1>Schön, dass du da bist.</h1>
            <p>In zwei kurzen Schritten lernen wir dich und deine Wünsche kennen.</p>

            <div class="guest-journey" aria-label="Formularfortschritt">
              <div class="guest-journey__item guest-journey__item--active">
                <span class="guest-journey__number">
                  <v-icon v-if="step > 1" small color="white">mdi-check</v-icon>
                  <template v-else>1</template>
                </span>
                <div>
                  <strong>Kontaktdaten</strong>
                  <small>Damit wir dich erreichen können</small>
                </div>
              </div>
              <div class="guest-journey__line" :class="{ 'guest-journey__line--active': step >= 2 }"></div>
              <div class="guest-journey__item" :class="{ 'guest-journey__item--active': step >= 2 }">
                <span class="guest-journey__number">2</span>
                <div>
                  <strong>Wünsche & Ziele</strong>
                  <small>Damit dein Start zu dir passt</small>
                </div>
              </div>
            </div>

            <div class="guest-story__brand">
              <img class="guest-story__brand-logo" :src="brandLogo" alt="WELL COME FIT" />
            </div>
          </aside>

          <section class="guest-form-panel">
            <div class="guest-form-heading">
              <div class="guest-form-heading__topline">
                <span>Schritt {{ step }} von 2</span>
                <span class="guest-form-heading__counter">{{ step }}/2</span>
              </div>
              <h2>{{ step === 1 ? "Wie dürfen wir dich kennenlernen?" : "Was möchtest du erreichen?" }}</h2>
              <p>{{ step === 1 ? "Trage bitte deine Kontaktdaten ein." : "Wähle alles aus, was zu dir und deinem Start passt." }}</p>
            </div>

            <v-window v-model="step" :touch="false" class="guest-form-window">
              <v-window-item :value="1">
                <v-form ref="contactForm" @submit.prevent="goToGoals">
                  <v-row dense class="guest-contact-grid">
                    <v-col cols="12" sm="6">
                      <v-text-field
                        v-model.trim="form.firstName"
                        outlined
                        color="teal darken-1"
                        class="guest-input"
                        label="Vorname*"
                        maxlength="80"
                        autocomplete="given-name"
                        prepend-inner-icon="mdi-account-outline"
                        :rules="[rules.required]" />
                    </v-col>
                    <v-col cols="12" sm="6">
                      <v-text-field
                        v-model.trim="form.lastName"
                        outlined
                        color="teal darken-1"
                        class="guest-input"
                        label="Nachname*"
                        maxlength="80"
                        autocomplete="family-name"
                        prepend-inner-icon="mdi-account-box-outline"
                        :rules="[rules.required]" />
                    </v-col>
                    <v-col cols="12" sm="6">
                      <v-text-field
                        v-model.trim="form.phone"
                        outlined
                        color="teal darken-1"
                        class="guest-input"
                        label="Telefonnummer*"
                        type="tel"
                        maxlength="40"
                        autocomplete="tel"
                        prepend-inner-icon="mdi-phone-outline"
                        :rules="[rules.required, rules.phone]" />
                    </v-col>
                    <v-col cols="12" sm="6">
                      <v-text-field
                        v-model.trim="form.email"
                        outlined
                        color="teal darken-1"
                        class="guest-input"
                        label="E-Mail*"
                        type="email"
                        maxlength="254"
                        autocomplete="email"
                        prepend-inner-icon="mdi-email-outline"
                        :rules="[rules.required, rules.email]" />
                    </v-col>
                  </v-row>

                  <div class="guest-actions guest-actions--end">
                    <v-btn type="submit" x-large depressed rounded color="primary" class="guest-primary-btn">
                      Weiter zu deinen Zielen
                      <v-icon right>mdi-arrow-right</v-icon>
                    </v-btn>
                  </div>
                </v-form>
              </v-window-item>

              <v-window-item :value="2">
                <v-form ref="goalForm" @submit.prevent="submitGuestbook">
                  <div class="guest-question guest-question--source">
                    <div class="guest-question__heading">
                      <span class="guest-question__icon"><v-icon color="teal darken-1" size="21">mdi-bullhorn-outline</v-icon></span>
                      <div>
                        <strong>Wie bist du auf uns aufmerksam geworden?</strong>
                        <small>Bitte wähle eine Antwort aus.</small>
                      </div>
                    </div>
                    <v-select
                      v-model="form.referralSource"
                      :items="sourceOptions"
                      :rules="[rules.required]"
                      :menu-props="sourceMenuProps"
                      outlined
                      color="teal darken-1"
                      item-color="teal darken-1"
                      class="guest-input guest-source-select"
                      label="Herkunft auswählen*"
                      prepend-inner-icon="mdi-chevron-down-circle-outline" />

                    <v-text-field
                      v-if="form.referralSource === 'Andere'"
                      v-model.trim="form.referralSourceOther"
                      :rules="[rules.required]"
                      outlined
                      color="teal darken-1"
                      class="guest-input"
                      maxlength="160"
                      prepend-inner-icon="mdi-pencil-outline"
                      label="Andere Herkunft*" />
                  </div>

                  <fieldset class="guest-goals" :class="{ 'guest-goals--error': goalError }">
                    <legend>
                      <span>Welche Ziele hast du?*</span>
                      <small>{{ form.goals.length ? form.goals.length + " ausgewählt" : "Mehrfachauswahl möglich" }}</small>
                    </legend>
                    <div class="guest-goals__grid">
                      <button
                        v-for="goal in goalOptions"
                        :key="goal.value"
                        type="button"
                        class="guest-goal"
                        :class="{ 'guest-goal--selected': form.goals.includes(goal.value) }"
                        :aria-pressed="form.goals.includes(goal.value)"
                        @click="toggleGoal(goal.value)">
                        <span class="guest-goal__icon">
                          <v-icon color="teal darken-1" size="24">{{ goal.icon }}</v-icon>
                        </span>
                        <span class="guest-goal__label">{{ goal.text }}</span>
                        <v-icon class="guest-goal__check" size="21">
                          {{ form.goals.includes(goal.value) ? "mdi-check-circle" : "mdi-circle-outline" }}
                        </v-icon>
                      </button>
                    </div>
                    <div v-if="goalError" class="guest-field-error">Bitte wähle mindestens ein Trainingsziel aus.</div>
                  </fieldset>

                  <v-text-field
                    v-if="form.goals.includes('other')"
                    v-model.trim="form.goalOther"
                    :rules="[rules.required]"
                    outlined
                    color="teal darken-1"
                    maxlength="250"
                    class="guest-input mt-6"
                    prepend-inner-icon="mdi-pencil-outline"
                    label="Anderes Ziel*" />

                  <div class="guest-privacy">
                    <v-icon color="teal darken-1" size="20">mdi-lock-outline</v-icon>
                    <span>Mit dem Absenden bestätigst du, dass wir deine Angaben für deine persönliche Beratung verwenden dürfen.</span>
                  </div>

                  <v-alert v-if="submitError" type="error" text dense class="mb-5">{{ submitError }}</v-alert>

                  <div class="guest-actions">
                    <v-btn text x-large rounded class="guest-back-btn" :disabled="submitting" @click="backToContact">
                      <v-icon left>mdi-arrow-left</v-icon>
                      Zurück
                    </v-btn>
                    <v-btn type="submit" x-large depressed rounded color="primary" class="guest-primary-btn" :loading="submitting">
                      Angaben absenden
                      <v-icon right>mdi-send-outline</v-icon>
                    </v-btn>
                  </div>
                </v-form>
              </v-window-item>
            </v-window>
          </section>
        </div>
      </v-card>
    </div>

    <div v-else-if="stage === 'handoff'" class="guestbook-stage guestbook-stage--centered">
      <v-card class="handoff-card pa-7 pa-sm-11" rounded="xl">
        <div class="handoff-card__check mb-6">
          <v-icon color="white" size="52">mdi-check</v-icon>
        </div>
        <div class="guest-eyebrow guest-eyebrow--green">VIELEN DANK</div>
        <h1>Danke für all deine Angaben.</h1>
        <p>Alles ist gespeichert. Dein Trainer macht gleich gemeinsam mit dir weiter.</p>

        <div class="handoff-card__divider"></div>

        <div class="handoff-card__trainer">
          <div class="handoff-card__trainer-icon" :class="{ 'handoff-card__trainer-icon--locked': handoffLocked }">
            <v-icon size="30">{{ handoffLocked ? "mdi-lock" : "mdi-account-tie-outline" }}</v-icon>
          </div>
          <div>
            <strong>Alles ist bereit</strong>
            <small>Einen kleinen Moment bitte – dein Trainer ist gleich für dich da.</small>
          </div>
        </div>

        <v-btn x-large block depressed rounded color="primary" class="staff-primary-btn mt-7" @click="continueToTrainer">
          <v-icon left>{{ handoffLocked ? "mdi-lock-open-outline" : "mdi-arrow-right" }}</v-icon>
          Trainer übernimmt
        </v-btn>
      </v-card>
    </div>

    <div v-else-if="stage === 'trainer'" class="guestbook-stage guestbook-stage--centered">
      <v-card class="staff-card pa-7 pa-sm-10" rounded="xl">
        <div class="staff-card__icon mb-5">
          <v-icon color="teal darken-1" size="38">mdi-account-check-outline</v-icon>
        </div>
        <h1 class="staff-card__title">Gästebuch bereit</h1>
        <p class="staff-card__copy">Ordne jetzt den Kundenaccount zu. Danach öffnet sich der Erstgespräch-Termin automatisch.</p>
        <v-btn x-large block depressed rounded color="primary" class="staff-primary-btn" @click="guestbookDialog = true">
          Account-Zuordnung öffnen
        </v-btn>
        <v-btn text block class="mt-3" @click="continueInCalendar">Im Kalender fortsetzen</v-btn>
      </v-card>
    </div>

    <v-dialog v-model="pinDialog" max-width="430" persistent>
      <v-card class="pin-card pa-6 pa-sm-8" rounded="xl">
        <div class="d-flex align-start justify-space-between mb-5">
          <div class="pin-card__icon">
            <v-icon color="teal darken-1" size="30">mdi-shield-lock-outline</v-icon>
          </div>
          <v-btn icon :disabled="pinLoading" aria-label="PIN-Eingabe schliessen" @click="closePinDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
        <h2>Trainerbereich entsperren</h2>
        <p>Bitte gib die 4-stellige Mitarbeiter-PIN ein.</p>
        <v-form @submit.prevent="verifyPin">
          <v-text-field
            ref="pinField"
            v-model="pin"
            type="password"
            inputmode="numeric"
            pattern="[0-9]*"
            maxlength="4"
            outlined
            autofocus
            color="teal darken-1"
            class="pin-input"
            label="Mitarbeiter-PIN"
            :error-messages="pinError ? [pinError] : []"
            @input="normalizePin" />
          <v-btn type="submit" x-large block depressed rounded color="primary" :loading="pinLoading" :disabled="pin.length !== 4">
            Entsperren
          </v-btn>
        </v-form>
      </v-card>
    </v-dialog>

    <work-calendar-guestbook-dialog
      v-model="guestbookDialog"
      :entry="entry"
      source-label="Studio · Gästebuch"
      :assigned-employee-id="selectedTrainerId"
      :assigned-employee-name="selectedTrainerName"
      @resolved="handleGuestbookResolved"
      @refresh="continueInCalendar" />
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex"
import api from "@/api"
import { brandLogo } from "@/constants/brandAssets"
import WorkCalendarGuestbookDialog from "@/views/dashboard/workCalendar/WorkCalendarGuestbookDialog.vue"

const HANDOFF_STORAGE_KEY = "guestbook_entry_handoff_v1"

const SOURCE_OPTIONS = [
  "Aussenwerbung",
  "Bereits bekannt",
  "Empfehlung",
  "Facebook",
  "Flyer",
  "Google Suche",
  "Google Werbung",
  "Instagram",
  "Messe / Ausstellung",
  "Tik Tok",
  "Zeitung",
  "Andere",
]

const GOAL_OPTIONS = [
  { text: "Abnehmen", value: "weight_loss", icon: "mdi-scale-bathroom" },
  { text: "Muskelaufbau", value: "muscle_gain", icon: "mdi-arm-flex-outline" },
  { text: "Straffen", value: "toning", icon: "mdi-dumbbell" },
  { text: "Ausdauer verbessern", value: "endurance", icon: "mdi-run-fast" },
  { text: "Rücken kräftigen", value: "back_strength", icon: "mdi-human-handsup" },
  { text: "Schmerzen beseitigen", value: "pain_relief", icon: "mdi-bandage" },
  { text: "Bewegungsdrang ausleben", value: "movement", icon: "mdi-run" },
  { text: "Beweglichkeit verbessern", value: "mobility", icon: "mdi-human-handsup" },
  { text: "Koordination verbessern", value: "coordination", icon: "mdi-axis-arrow" },
  { text: "Gesund bleiben", value: "health", icon: "mdi-heart-pulse" },
  { text: "Haltung verbessern", value: "posture", icon: "mdi-human-male-height-variant" },
  { text: "Andere", value: "other", icon: "mdi-dots-horizontal-circle-outline" },
]

function emptyForm() {
  return {
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
    referralSource: null,
    referralSourceOther: "",
    goals: [],
    goalOther: "",
  }
}

export default {
  name: "GuestbookEntryPage",
  components: { WorkCalendarGuestbookDialog },
  data() {
    return {
      brandLogo,
      contextLoading: true,
      contextError: "",
      context: { trainer: { name: "" }, studio: { name: "" } },
      stage: "start",
      step: 1,
      form: emptyForm(),
      sourceOptions: SOURCE_OPTIONS,
      sourceMenuProps: {
        contentClass: "guestbook-source-menu",
        offsetY: true,
        maxHeight: 360,
        zIndex: 120,
      },
      goalOptions: GOAL_OPTIONS,
      goalError: false,
      submitting: false,
      submitError: "",
      handoffLocked: false,
      entry: null,
      pinDialog: false,
      pinPurpose: "handoff",
      pin: "",
      pinError: "",
      pinLoading: false,
      guestbookDialog: false,
      allowNavigation: false,
      rules: {
        required: (value) => Boolean(String(value || "").trim()) || "Dieses Feld ist erforderlich.",
        email: (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || "").trim()) || "Bitte gib eine gültige E-Mail-Adresse ein.",
        phone: (value) => /^[0-9+()\s./-]{5,40}$/.test(String(value || "").trim()) || "Bitte gib eine gültige Telefonnummer ein.",
      },
    }
  },
  computed: {
    ...mapGetters("studioStore", ["getSelectedEmployee"]),
    selectedTrainerId() {
      const selectedUser = this.getSelectedEmployee?.user
      return String(selectedUser?._id || selectedUser || "")
    },
    selectedTrainerName() {
      const selectedEmployee = this.getSelectedEmployee || {}
      return (
        selectedEmployee.fullName ||
        `${selectedEmployee.vorname || ""} ${selectedEmployee.nachname || ""}`.trim() ||
        ""
      )
    },
    trainerDisplayName() {
      return this.selectedTrainerName || this.context.trainer.name || "Mitarbeiter"
    },
  },
  mounted() {
    this.setNavigationbarVisibility(false)
    this.restoreHandoffState()
    window.addEventListener("beforeunload", this.onBeforeUnload)
    this.loadContext()
  },
  beforeDestroy() {
    window.removeEventListener("beforeunload", this.onBeforeUnload)
    this.setNavigationbarVisibility(true)
  },
  beforeRouteLeave(to, from, next) {
    if (this.allowNavigation || !["guest", "handoff"].includes(this.stage)) return next()
    if (this.stage === "guest" && !this.handoffLocked) {
      this.clearHandoffState()
      return next()
    }
    next(false)
  },
  methods: {
    ...mapActions("navigationStoreNotPersisted", ["setNavigationbarVisibility"]),
    async loadContext() {
      this.contextLoading = true
      this.contextError = ""
      try {
        const result = await api.fetchGuestbookStaffContext()
        this.context = result
      } catch (error) {
        this.contextError = error?.response?.data?.message || "Der Trainerbereich konnte nicht geladen werden."
      } finally {
        this.contextLoading = false
      }
    },
    beginGuestMode() {
      this.form = emptyForm()
      this.step = 1
      this.goalError = false
      this.submitError = ""
      this.entry = null
      this.handoffLocked = false
      this.stage = "guest"
      this.persistHandoffState()
    },
    async goToGoals() {
      const valid = this.$refs.contactForm && this.$refs.contactForm.validate()
      if (!valid) return
      this.step = 2
      this.scrollToTop()
    },
    backToContact() {
      this.step = 1
      this.scrollToTop()
    },
    toggleGoal(goal) {
      const index = this.form.goals.indexOf(goal)
      if (index === -1) this.form.goals.push(goal)
      else this.form.goals.splice(index, 1)
      if (goal === "other" && !this.form.goals.includes("other")) this.form.goalOther = ""
      this.goalError = false
    },
    async submitGuestbook() {
      this.goalError = this.form.goals.length === 0
      const valid = this.$refs.goalForm && this.$refs.goalForm.validate()
      if (!valid || this.goalError || this.submitting) return

      this.submitting = true
      this.submitError = ""
      try {
        const result = await api.createGuestbookStaffEntry({
          ...this.form,
          goals: [...this.form.goals],
          handoffLocked: this.handoffLocked,
          referralSourceOther: this.form.referralSource === "Andere" ? this.form.referralSourceOther : "",
          goalOther: this.form.goals.includes("other") ? this.form.goalOther : "",
        })
        if (!result || !result.ok || !result.entry) throw new Error("Invalid guestbook response")
        this.entry = result.entry
        this.stage = "handoff"
        this.persistHandoffState()
        this.scrollToTop()
      } catch (error) {
        this.submitError = error?.response?.data?.message || "Deine Angaben konnten nicht gespeichert werden. Bitte versuche es nochmals."
      } finally {
        this.submitting = false
      }
    },
    toggleProtection() {
      if (!this.handoffLocked) {
        this.handoffLocked = true
        this.persistHandoffState()
        return
      }
      this.openPinDialog("unlock-protection")
    },
    continueToTrainer() {
      if (this.handoffLocked) {
        this.openPinDialog("handoff")
        return
      }
      this.openTrainerCockpit(this.entry)
    },
    openPinDialog(purpose) {
      this.pinPurpose = purpose
      this.pin = ""
      this.pinError = ""
      this.pinDialog = true
      this.$nextTick(() => this.$refs.pinField && this.$refs.pinField.focus())
    },
    closePinDialog(force = false) {
      if (this.pinLoading && !force) return
      this.pinDialog = false
      this.pin = ""
      this.pinError = ""
    },
    normalizePin(value) {
      this.pin = String(value || "")
        .replace(/\D/g, "")
        .slice(0, 4)
      this.pinError = ""
    },
    async verifyPin() {
      if (this.pin.length !== 4 || this.pinLoading) return
      this.pinLoading = true
      this.pinError = ""
      try {
        const entryReference = this.pinPurpose === "handoff" ? this.entry : null
        const result = await api.unlockGuestbookStaffView(this.pin, entryReference)
        if (!result || !result.ok) throw new Error("Invalid unlock response")
        const purpose = this.pinPurpose
        this.closePinDialog(true)
        if (purpose === "unlock-protection") {
          this.handoffLocked = false
          this.persistHandoffState()
          return
        }
        this.openTrainerCockpit(result.entry || this.entry)
      } catch (error) {
        this.pinError = error?.response?.data?.message || "Die Mitarbeiter-PIN konnte nicht geprüft werden."
      } finally {
        this.pinLoading = false
      }
    },
    openTrainerCockpit(entry) {
      if (!entry || !entry._id) {
        this.$toast?.error("Der Gästebuch-Eintrag konnte nicht geladen werden. Bitte öffne ihn im Kalender.")
        this.continueInCalendar()
        return
      }
      this.entry = entry
      this.handoffLocked = false
      this.stage = "trainer"
      this.clearHandoffState()
      this.guestbookDialog = true
    },
    handleGuestbookResolved(result = {}) {
      const appointment = result.appointment
      if (!appointment || !appointment._id) {
        this.continueInCalendar()
        return
      }
      this.clearHandoffState()
      this.allowNavigation = true
      this.$router.push({
        name: "workCalendar",
        query: {
          date: appointment.date || this.entry?.walkInCalendar?.date,
          appointmentId: appointment._id,
        },
      })
    },
    continueInCalendar() {
      const date = this.entry?.walkInCalendar?.date || this.$moment().format("YYYY-MM-DD")
      this.clearHandoffState()
      this.allowNavigation = true
      this.$router.push({ name: "workCalendar", query: { date } })
    },
    leaveToDashboard() {
      this.clearHandoffState()
      this.allowNavigation = true
      this.$router.push({ name: "dashboardHome" })
    },
    persistHandoffState() {
      if (typeof sessionStorage === "undefined") return
      if (!["guest", "handoff"].includes(this.stage)) {
        this.clearHandoffState()
        return
      }
      const snapshot = {
        stage: this.stage,
        handoffLocked: this.handoffLocked,
        entry: this.stage === "handoff" ? this.entry : null,
      }
      sessionStorage.setItem(HANDOFF_STORAGE_KEY, JSON.stringify(snapshot))
    },
    restoreHandoffState() {
      if (typeof sessionStorage === "undefined") return
      try {
        const snapshot = JSON.parse(sessionStorage.getItem(HANDOFF_STORAGE_KEY) || "null")
        if (!snapshot || !["guest", "handoff"].includes(snapshot.stage)) return
        this.stage = snapshot.stage
        this.handoffLocked = snapshot.handoffLocked === true
        this.entry = snapshot.entry || null
        if (this.stage === "handoff" && (!this.entry || !this.entry._id)) {
          this.clearHandoffState()
          this.stage = "start"
        }
      } catch (error) {
        this.clearHandoffState()
      }
    },
    clearHandoffState() {
      if (typeof sessionStorage !== "undefined") sessionStorage.removeItem(HANDOFF_STORAGE_KEY)
    },
    onBeforeUnload(event) {
      if (!["guest", "handoff"].includes(this.stage)) return
      event.preventDefault()
      event.returnValue = ""
    },
    scrollToTop() {
      this.$nextTick(() => {
        const panel = this.$el && this.$el.querySelector ? this.$el.querySelector(".guest-form-panel") : null
        if (panel && typeof panel.scrollTo === "function") panel.scrollTo({ top: 0, behavior: "smooth" })
        const root = this.$el
        if (root && typeof root.scrollTo === "function") root.scrollTo({ top: 0, behavior: "smooth" })
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.guestbook-intake {
  --guestbook-green: #009b52;
  --guestbook-green-dark: #006f3a;
  position: fixed;
  inset: 0;
  z-index: 40;
  min-height: 100vh;
  min-height: 100dvh;
  overflow-x: hidden;
  overflow-y: auto;
  color: #102118;
  background:
    radial-gradient(circle at 10% 10%, rgba(24, 203, 116, 0.2), transparent 34%),
    radial-gradient(circle at 92% 88%, rgba(0, 122, 67, 0.26), transparent 32%),
    linear-gradient(145deg, #06110b 0%, #0b2115 54%, #07140d 100%);
  -webkit-overflow-scrolling: touch;
}

.guestbook-intake__orb {
  position: fixed;
  width: 340px;
  height: 340px;
  border-radius: 50%;
  filter: blur(80px);
  opacity: 0.25;
  pointer-events: none;
}

.guestbook-intake__orb--one {
  top: -180px;
  right: 5%;
  background: #29df8b;
}

.guestbook-intake__orb--two {
  bottom: -200px;
  left: 2%;
  background: #006f3a;
}

.guestbook-loading,
.guestbook-stage {
  position: relative;
  z-index: 1;
}

.guestbook-loading {
  min-height: 100vh;
  min-height: 100dvh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 18px;
  color: white;
  font-weight: 800;
}

.guestbook-stage {
  width: min(100%, 1120px);
  min-height: 100vh;
  min-height: 100dvh;
  margin: 0 auto;
  padding: max(22px, env(safe-area-inset-top)) max(22px, env(safe-area-inset-right)) max(22px, env(safe-area-inset-bottom))
    max(22px, env(safe-area-inset-left));
}

.guestbook-stage--centered {
  display: flex;
  align-items: center;
  justify-content: center;
}

.staff-card,
.handoff-card,
.pin-card {
  width: min(100%, 720px);
  border: 1px solid rgba(255, 255, 255, 0.42) !important;
  box-shadow: 0 34px 90px rgba(0, 0, 0, 0.38) !important;
}

.staff-brand {
  display: flex;
  align-items: center;
  gap: 18px;
}

.staff-brand__mark,
.guest-header__mark {
  display: grid;
  place-items: center;
  flex: 0 0 auto;
  background: linear-gradient(145deg, var(--guestbook-green), var(--guestbook-green-dark));
  box-shadow: 0 15px 30px rgba(0, 128, 69, 0.25);
}

.staff-brand__mark {
  width: 64px;
  height: 64px;
  border-radius: 19px;
}

.staff-eyebrow,
.guest-eyebrow {
  color: #008548;
  font-size: 0.72rem;
  font-weight: 950;
  letter-spacing: 0.13em;
  text-transform: uppercase;
}

.staff-card__title {
  margin: 0 0 12px;
  font-size: clamp(1.85rem, 4vw, 2.55rem);
  line-height: 1.08;
  letter-spacing: -0.035em;
}

.staff-card__copy {
  color: #607067;
  font-size: 1rem;
  line-height: 1.65;
}

.staff-card__copy--lead {
  font-size: 1.08rem;
}

.staff-card__icon,
.pin-card__icon {
  width: 62px;
  height: 62px;
  display: grid;
  place-items: center;
  border-radius: 18px;
  background: #e6f7ef;
}

.staff-card__icon--error {
  background: #ffebee;
}

.staff-context {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.staff-context__item {
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 13px;
  padding: 16px;
  border: 1px solid #dbe9e1;
  border-radius: 16px;
  background: #f7fbf9;
}

.staff-context__item div {
  min-width: 0;
}

.staff-context__item small,
.staff-context__item strong {
  display: block;
}

.staff-context__item small {
  color: #7b8b82;
  font-size: 0.72rem;
  font-weight: 800;
  text-transform: uppercase;
}

.staff-context__item strong {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.staff-flow {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.staff-flow__step {
  display: flex;
  align-items: center;
  gap: 9px;
  color: #3e5147;
  font-size: 0.78rem;
}

.staff-flow__step span {
  width: 28px;
  height: 28px;
  display: grid;
  place-items: center;
  flex: 0 0 auto;
  border-radius: 50%;
  color: white;
  background: var(--guestbook-green);
}

.staff-primary-btn,
.guest-primary-btn {
  min-height: 56px !important;
  font-weight: 900 !important;
  letter-spacing: 0 !important;
  text-transform: none !important;
}

.guestbook-stage--guest {
  display: flex;
  align-items: flex-start;
}

.guest-card {
  width: 100%;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.38) !important;
  box-shadow: 0 34px 90px rgba(0, 0, 0, 0.38) !important;
}

.guest-header {
  min-height: 82px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  padding: 16px 24px;
  color: white;
  background: #081b11;
}

.guest-header__brand {
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 13px;
}

.guest-header__mark {
  width: 46px;
  height: 46px;
  border-radius: 14px;
}

.guest-header .guest-eyebrow {
  color: #65dca3;
  font-size: 0.62rem;
}

.guest-header strong {
  display: block;
  overflow: hidden;
  font-size: 1.05rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.guest-protection {
  display: flex;
  align-items: center;
  gap: 10px;
}

.guest-protection__label {
  color: #9be8c3;
  font-size: 0.72rem;
  font-weight: 900;
  text-transform: uppercase;
}

.guest-protection__button {
  color: rgba(255, 255, 255, 0.82) !important;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.15);
}

.guest-protection__button--locked {
  color: white !important;
  background: var(--guestbook-green);
  border-color: rgba(255, 255, 255, 0.28);
  box-shadow: 0 8px 22px rgba(0, 155, 82, 0.32);
}

.guest-experience {
  display: grid;
  grid-template-columns: minmax(285px, 0.76fr) minmax(0, 1.55fr);
  min-height: 590px;
}

.guest-story {
  position: relative;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 44px 38px 38px;
  color: white;
  background:
    radial-gradient(circle at 110% 5%, rgba(142, 244, 194, 0.27), transparent 38%),
    radial-gradient(circle at -15% 92%, rgba(0, 69, 36, 0.58), transparent 42%),
    linear-gradient(155deg, #008f4c 0%, #006f3b 53%, #07331f 100%);
}

.guest-story::after {
  content: "";
  position: absolute;
  right: -86px;
  bottom: -94px;
  width: 250px;
  height: 250px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 50%;
  box-shadow: 0 0 0 38px rgba(255, 255, 255, 0.035), 0 0 0 78px rgba(255, 255, 255, 0.025);
  pointer-events: none;
}

.guest-story > * {
  position: relative;
  z-index: 1;
}

.guest-story__eyebrow {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #b8f1d4;
  font-size: 0.69rem;
  font-weight: 950;
  letter-spacing: 0.13em;
  text-transform: uppercase;
}

.guest-story h1 {
  max-width: 340px;
  margin: 22px 0 13px;
  font-size: clamp(2.35rem, 4.2vw, 3.4rem);
  line-height: 0.98;
  letter-spacing: -0.055em;
}

.guest-story > p {
  max-width: 330px;
  margin: 0;
  color: rgba(255, 255, 255, 0.76);
  font-size: 0.98rem;
  line-height: 1.6;
}

.guest-journey {
  margin-top: 42px;
}

.guest-journey__item {
  display: flex;
  align-items: center;
  gap: 14px;
  color: rgba(255, 255, 255, 0.54);
}

.guest-journey__number {
  width: 40px;
  height: 40px;
  display: grid;
  place-items: center;
  flex: 0 0 auto;
  border: 1px solid rgba(255, 255, 255, 0.28);
  border-radius: 13px;
  color: rgba(255, 255, 255, 0.72);
  background: rgba(0, 0, 0, 0.1);
  font-size: 0.8rem;
  font-weight: 900;
}

.guest-journey__item strong,
.guest-journey__item small {
  display: block;
}

.guest-journey__item strong {
  font-size: 0.91rem;
}

.guest-journey__item small {
  margin-top: 2px;
  color: inherit;
  font-size: 0.69rem;
}

.guest-journey__item--active {
  color: white;
}

.guest-journey__item--active .guest-journey__number {
  color: white;
  border-color: rgba(255, 255, 255, 0.65);
  background: rgba(255, 255, 255, 0.16);
  box-shadow: 0 9px 22px rgba(0, 54, 28, 0.19);
}

.guest-journey__line {
  width: 2px;
  height: 29px;
  margin: 7px 0 7px 19px;
  background: rgba(255, 255, 255, 0.2);
}

.guest-journey__line--active {
  background: #8ce8ba;
}

.guest-story__brand {
  display: flex;
  align-items: center;
  margin-top: auto;
  padding-top: 28px;
}

.guest-story__brand-logo {
  width: 116px;
  max-width: 100%;
  height: auto;
  display: block;
  object-fit: contain;
}

.guest-form-panel {
  min-width: 0;
  min-height: 0;
  padding: 40px 42px 44px;
  background: #f8fbf9;
}

.guest-form-window {
  min-width: 0;
  margin: -2px;
  padding: 2px;
  overflow: visible !important;
}

.guest-form-window::v-deep .v-window__container {
  overflow: visible;
}

.guest-contact-grid {
  width: 100%;
  margin: 0 !important;
}

.guest-contact-grid > [class*="col-"] {
  min-width: 0;
  padding: 6px !important;
}

.guest-form-heading {
  margin-bottom: 28px;
}

.guest-form-heading__topline {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.guest-form-heading__topline > span:first-child {
  color: #008548;
  font-size: 0.7rem;
  font-weight: 950;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.guest-form-heading__counter {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 42px;
  height: 27px;
  padding: 0 10px;
  border: 1px solid #cee5d8;
  border-radius: 999px;
  color: #007c43;
  background: #eaf7f0;
  font-size: 0.68rem;
  font-weight: 900;
}

.guest-form-heading h2 {
  max-width: 650px;
  margin: 8px 0 6px;
  color: #15271d;
  font-size: clamp(1.7rem, 3.1vw, 2.35rem);
  line-height: 1.1;
  letter-spacing: -0.035em;
}

.guest-form-heading p {
  margin: 0;
  color: #6b7c72;
  line-height: 1.5;
}

.guest-input::v-deep .v-input__slot {
  min-height: 58px !important;
  border-radius: 15px !important;
  background: #ffffff !important;
}

.guest-input::v-deep fieldset {
  border-color: #d6e3dc !important;
}

.guest-input.v-input--is-focused::v-deep fieldset {
  border-color: #008548 !important;
  border-width: 2px !important;
}

.guest-input::v-deep .v-icon {
  color: #74867b;
}

.guest-source-select::v-deep .v-input__append-inner .v-icon {
  color: #008548 !important;
}

.guest-question {
  margin-bottom: 27px;
  padding: 18px 18px 3px;
  border: 1px solid #dce8e1;
  border-radius: 18px;
  background: #ffffff;
  box-shadow: 0 8px 25px rgba(23, 57, 39, 0.045);
}

.guest-question__heading {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 15px;
}

.guest-question__icon {
  width: 40px;
  height: 40px;
  display: grid;
  place-items: center;
  flex: 0 0 auto;
  border-radius: 12px;
  background: #e8f6ef;
}

.guest-question__heading strong,
.guest-question__heading small {
  display: block;
}

.guest-question__heading strong {
  color: #20352a;
  font-size: 0.91rem;
}

.guest-question__heading small {
  margin-top: 2px;
  color: #718278;
  font-size: 0.7rem;
}

.guest-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-top: 26px;
}

.guest-actions--end {
  justify-content: flex-end;
}

.guest-back-btn {
  color: #52655a !important;
  font-weight: 800 !important;
  letter-spacing: 0 !important;
  text-transform: none !important;
}

.guest-goals {
  min-width: 0;
  margin: 0;
  padding: 0;
  border: 0;
}

.guest-goals legend {
  width: 100%;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 14px;
  margin-bottom: 14px;
  color: #20352a;
}

.guest-goals legend span {
  font-size: 0.96rem;
  font-weight: 900;
}

.guest-goals legend small {
  color: #6f8277;
  font-size: 0.68rem;
  font-weight: 800;
}

.guest-goals__grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 11px;
}

.guest-goal {
  position: relative;
  min-height: 76px;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 37px 12px 12px;
  border: 1px solid #d8e5de;
  border-radius: 16px;
  color: #26392f;
  background: white;
  cursor: pointer;
  font: inherit;
  font-weight: 800;
  text-align: left;
  transition: transform 160ms ease, border-color 160ms ease, background 160ms ease, box-shadow 160ms ease;
}

.guest-goal:active {
  transform: scale(0.985);
}

.guest-goal--selected {
  color: #006f3a;
  border-color: #008548;
  background: linear-gradient(145deg, #effaf4, #e1f5eb);
  box-shadow: 0 10px 24px rgba(0, 133, 72, 0.11);
}

.guest-goal__icon {
  width: 39px;
  height: 39px;
  display: grid;
  place-items: center;
  flex: 0 0 auto;
  border-radius: 12px;
  background: #edf6f1;
}

.guest-goal--selected .guest-goal__icon {
  background: #d4f1e2;
}

.guest-goal__label {
  min-width: 0;
  line-height: 1.2;
}

.guest-goal__check {
  position: absolute !important;
  top: 12px;
  right: 11px;
  color: #9caf9f !important;
}

.guest-goal--selected .guest-goal__check {
  color: #008548 !important;
}

.guest-goals--error .guest-goals__grid {
  padding: 4px;
  border: 1px solid #e53935;
  border-radius: 18px;
}

.guest-field-error {
  margin-top: 7px;
  color: #c62828;
  font-size: 0.75rem;
}

.guest-privacy {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin: 24px 0 0;
  padding: 12px 14px;
  border-radius: 13px;
  color: #66786e;
  background: #edf5f1;
  font-size: 0.72rem;
  line-height: 1.45;
}

.handoff-card {
  text-align: center;
}

.handoff-card__check {
  width: 86px;
  height: 86px;
  display: grid;
  place-items: center;
  margin: 0 auto;
  border-radius: 28px;
  background: linear-gradient(145deg, #0bc16a, #007c43);
  box-shadow: 0 18px 36px rgba(0, 155, 82, 0.28);
}

.guest-eyebrow--green {
  color: #008548;
}

.handoff-card h1 {
  margin: 9px 0 12px;
  font-size: clamp(2rem, 5vw, 3.15rem);
  line-height: 1.08;
  letter-spacing: -0.045em;
}

.handoff-card > p {
  margin: 0;
  color: #65746c;
  font-size: 1.1rem;
}

.handoff-card__divider {
  width: 80px;
  height: 1px;
  margin: 30px auto;
  background: #dce7e1;
}

.handoff-card__trainer {
  display: flex;
  align-items: center;
  gap: 15px;
  padding: 16px;
  border: 1px solid #dce8e1;
  border-radius: 17px;
  text-align: left;
  background: #f7fbf9;
}

.handoff-card__trainer-icon {
  width: 52px;
  height: 52px;
  display: grid;
  place-items: center;
  flex: 0 0 auto;
  border-radius: 15px;
  color: #007c43;
  background: #dff6eb;
}

.handoff-card__trainer-icon--locked {
  color: white;
  background: #0b2115;
}

.handoff-card__trainer strong,
.handoff-card__trainer small {
  display: block;
}

.handoff-card__trainer small {
  margin-top: 3px;
  color: #718078;
}

.pin-card h2 {
  margin: 0 0 7px;
  font-size: 1.7rem;
  letter-spacing: -0.025em;
}

.pin-card p {
  margin: 0 0 23px;
  color: #6d7b73;
}

.pin-input::v-deep input {
  font-size: 1.7rem;
  font-weight: 900;
  letter-spacing: 0.34em;
  text-align: center;
}

@media (max-width: 960px) {
  .guest-experience {
    grid-template-columns: 1fr;
    min-height: 0;
  }

  .guest-story {
    padding: 30px 34px;
  }

  .guest-story h1 {
    max-width: none;
    margin-top: 15px;
    font-size: clamp(2.1rem, 6vw, 2.9rem);
  }

  .guest-story > p {
    max-width: 640px;
  }

  .guest-journey {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 50px minmax(0, 1fr);
    align-items: center;
    margin-top: 24px;
  }

  .guest-journey__line {
    width: 100%;
    height: 2px;
    margin: 0;
  }

  .guest-form-panel {
    padding: 36px 34px 40px;
  }
}

@media (max-width: 800px) {
  .guest-goals__grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 600px) {
  .guestbook-stage {
    padding: max(10px, env(safe-area-inset-top)) max(10px, env(safe-area-inset-right)) max(10px, env(safe-area-inset-bottom))
      max(10px, env(safe-area-inset-left));
  }

  .staff-card,
  .handoff-card {
    padding: 26px 20px !important;
  }

  .staff-context {
    grid-template-columns: 1fr;
  }

  .staff-flow {
    align-items: flex-start;
    flex-direction: column;
  }

  .staff-flow > .v-icon {
    display: none;
  }

  .guest-header {
    min-height: 70px;
    padding: 12px 15px;
  }

  .guest-protection__label {
    display: none;
  }

  .guest-story {
    padding: 26px 20px;
  }

  .guest-story h1 {
    font-size: 2.2rem;
  }

  .guest-journey {
    display: block;
  }

  .guest-journey__line {
    width: 2px;
    height: 22px;
    margin: 5px 0 5px 19px;
  }

  .guest-form-panel {
    padding: 28px 20px 32px;
  }

  .guest-question {
    padding: 15px 14px 1px;
  }

  .guest-goals legend {
    align-items: flex-start;
    flex-direction: column;
    gap: 3px;
  }

  .guest-goals__grid {
    grid-template-columns: 1fr;
  }

  .guest-goal {
    min-height: 72px;
  }

  .guest-actions {
    align-items: stretch;
    flex-direction: column-reverse;
  }

  .guest-actions--end {
    align-items: stretch;
    flex-direction: column;
  }

  .guest-actions .v-btn {
    width: 100%;
  }
}

@media (min-width: 601px) and (max-width: 1366px) {
  .guestbook-intake--guest {
    overflow: hidden;
  }

  .guestbook-intake--guest .guestbook-intake__orb {
    display: none;
  }

  .guestbook-stage--guest {
    width: 100%;
    height: 100vh;
    height: 100dvh;
    min-height: 0;
    margin: 0;
    padding: 0;
  }

  .guest-card {
    height: 100%;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    border: 0 !important;
    border-radius: 0 !important;
    box-shadow: none !important;
  }

  .guest-header {
    min-height: 78px;
    flex: 0 0 auto;
    padding-top: max(14px, env(safe-area-inset-top));
    padding-right: max(24px, env(safe-area-inset-right));
    padding-bottom: 14px;
    padding-left: max(24px, env(safe-area-inset-left));
  }

  .guest-experience {
    min-height: 0;
    display: grid;
    flex: 1 1 auto;
    grid-template-columns: clamp(260px, 31vw, 390px) minmax(0, 1fr);
    overflow: hidden;
  }

  .guest-story {
    min-height: 0;
    padding: clamp(30px, 4vw, 48px) clamp(26px, 3.2vw, 42px) max(28px, env(safe-area-inset-bottom));
    overflow: hidden;
  }

  .guest-story h1 {
    max-width: 330px;
    margin: 20px 0 13px;
    font-size: clamp(2.15rem, 4.2vw, 3.3rem);
  }

  .guest-story > p {
    max-width: 325px;
  }

  .guest-journey {
    display: block;
    margin-top: clamp(28px, 4vh, 42px);
  }

  .guest-journey__line {
    width: 2px;
    height: 29px;
    margin: 7px 0 7px 19px;
  }

  .guest-form-panel {
    height: 100%;
    min-height: 0;
    padding: clamp(30px, 4vw, 48px) clamp(30px, 5vw, 64px) max(42px, env(safe-area-inset-bottom));
    overflow-x: hidden;
    overflow-y: auto;
    overscroll-behavior: contain;
    scrollbar-gutter: stable;
    -webkit-overflow-scrolling: touch;
  }
}

@media (min-width: 601px) and (max-width: 960px) {
  .guest-goals__grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (prefers-reduced-motion: reduce) {
  .guest-goal {
    transition: none;
  }

  .guest-goal:active {
    transform: none;
  }
}
</style>

<style lang="scss">
.guestbook-source-menu.v-menu__content {
  z-index: 120 !important;
  overflow: hidden;
  border: 1px solid #d4e3da;
  border-radius: 16px !important;
  background: #ffffff;
  box-shadow: 0 22px 55px rgba(4, 35, 19, 0.22) !important;
}

.guestbook-source-menu .v-list {
  padding: 8px;
  background: #ffffff !important;
}

.guestbook-source-menu .v-list-item {
  min-height: 46px;
  margin: 2px 0;
  border-radius: 10px;
  color: #20352a !important;
  font-weight: 750;
}

.guestbook-source-menu .v-list-item:hover {
  background: #f0f8f4 !important;
}

.guestbook-source-menu .v-list-item--active {
  color: #006f3a !important;
  background: #e2f5eb !important;
}
</style>
